import { useState, type ReactElement, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  Heart,
  Sparkles,
  AlertCircle,
  UserPlus,
  Coins,
  Check,
  BellOff,
  Trash2,
  type LucideIcon,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

import type { Notification, NotificationType } from "@/types/notification";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Notification icon configuration
 */
interface NotificationIconConfig {
  Icon: LucideIcon;
  color: string;
  bgColor: string;
}

/**
 * NotificationItem component props
 */
interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => Promise<boolean>;
  onClick: (notification: Notification) => void;
}

/**
 * NotificationBell component props
 */
interface NotificationBellProps {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  markAsRead: (id: string) => Promise<boolean>;
  markAllAsRead: () => Promise<boolean>;
  clearAll: () => Promise<boolean>;
}

/**
 * Helper to get full image URL (handles both local /static/ paths and external URLs)
 */
const getImageUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;
  if (url.startsWith("/static/")) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

/**
 * Helper to format relative time
 */
const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

/**
 * Icon mapping by notification type
 */
const getNotificationIcon = (type: NotificationType): NotificationIconConfig => {
  switch (type) {
    case "story_liked":
      return { Icon: Heart, color: "var(--st-primary)", bgColor: "#FFF5EE" };
    case "story_complete":
      return { Icon: Sparkles, color: "#FFB347", bgColor: "#FFF8E7" };
    case "story_failed":
      return { Icon: AlertCircle, color: "#EF4444", bgColor: "#FEF2F2" };
    case "new_follower":
      return { Icon: UserPlus, color: "#8B5CF6", bgColor: "#F5F3FF" };
    case "credits_added":
      return { Icon: Coins, color: "#10B981", bgColor: "#ECFDF5" };
    default:
      return { Icon: Bell, color: "var(--st-text-heading)", bgColor: "#F5F5F5" };
  }
};

/**
 * Individual notification item
 */
const NotificationItem = ({
  notification,
  onMarkAsRead,
  onClick,
}: NotificationItemProps): ReactElement => {
  const { Icon, color, bgColor } = getNotificationIcon(notification.type);
  const actorPicture = notification.data?.actor_picture as string | undefined;

  return (
    <div
      className={`flex items-start gap-3 p-3 cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-800 ${
        !notification.read ? "bg-orange-50/50 dark:bg-orange-900/20" : ""
      }`}
      style={{ borderBottom: "1px solid var(--st-border-light)" }}
      onClick={() => onClick(notification)}
    >
      {/* Icon or Avatar */}
      {actorPicture ? (
        <img
          src={getImageUrl(actorPicture) || ""}
          alt="Notification sender"
          className="w-10 h-10 rounded-full flex-shrink-0 object-cover"
        />
      ) : (
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: bgColor }}
        >
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
      )}

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p
          className="text-sm font-medium leading-tight"
          style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-heading)" }}
        >
          {notification.message}
        </p>
        <p
          className="text-xs mt-1"
          style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
        >
          {formatRelativeTime(notification.created_at)}
        </p>
      </div>

      {/* Unread indicator */}
      {!notification.read && (
        <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-2" />
      )}
    </div>
  );
};

/**
 * NotificationBell Component
 */
const NotificationBell = ({
  notifications,
  unreadCount,
  loading,
  markAsRead,
  markAllAsRead,
  clearAll,
}: NotificationBellProps): ReactElement => {
  const navigate = useNavigate();
  const [open, setOpen] = useState<boolean>(false);

  const handleNotificationClick = async (notification: Notification): Promise<void> => {
    // Mark as read
    if (!notification.read) {
      await markAsRead(notification.id);
    }

    // Close popover first to avoid DOM conflicts during navigation
    setOpen(false);

    // Navigate based on type after a brief delay to let popover close
    setTimeout(() => {
      if (notification.type === "story_liked" || notification.type === "story_complete") {
        const storyId = notification.data?.story_id as string | undefined;
        if (storyId) {
          navigate(`/story/${storyId}`);
        }
      } else if (notification.type === "new_follower") {
        const actorId = notification.data?.actor_id as string | undefined;
        if (actorId) {
          navigate(`/user/${actorId}`);
        }
      }
    }, 50);
  };

  const handleMarkAllAsRead = async (
    e: React.MouseEvent<HTMLButtonElement>
  ): Promise<void> => {
    e.stopPropagation();
    await markAllAsRead();
  };

  const handleClearAll = async (
    e: React.MouseEvent<HTMLButtonElement>
  ): Promise<void> => {
    e.stopPropagation();
    await clearAll();
  };

  const triggerStyle: CSSProperties = {
    background: open ? "rgba(255, 140, 97, 0.1)" : "transparent",
  };

  const badgeStyle: CSSProperties = {
    background: "#EF4444",
    color: "white",
    fontFamily: "Quicksand, sans-serif",
    fontSize: "11px",
  };

  const popoverStyle: CSSProperties = {
    background: "var(--st-bg-surface)",
    border: "2px solid var(--st-border-light)",
    borderRadius: "16px",
    boxShadow: "0 10px 40px rgba(92, 64, 51, 0.15)",
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative p-2 rounded-lg transition-colors hover:bg-white/50 dark:hover:bg-white/10 min-h-[44px] min-w-[44px] flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2"
          style={triggerStyle}
          aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : "Notifications"}
          aria-expanded={open}
          aria-haspopup="dialog"
        >
          <Bell
            className="w-6 h-6"
            style={{ color: open ? "var(--st-primary)" : "var(--st-text-heading)" }}
          />
          {/* Unread badge */}
          {unreadCount > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-xs font-bold flex items-center justify-center"
              style={badgeStyle}
              aria-hidden="true"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent className="w-80 p-0" align="end" style={popoverStyle}>
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-3"
          style={{ borderBottom: "2px solid var(--st-border-light)" }}
        >
          <h3
            className="text-lg font-bold"
            style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-heading)" }}
          >
            Notifications
          </h3>
          <div className="flex items-center gap-3">
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="flex items-center gap-1 text-xs font-medium transition-colors hover:text-orange-600 dark:hover:text-orange-400 focus:outline-none focus-visible:underline focus-visible:text-orange-600"
                style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-primary)" }}
              >
                <Check className="w-3 h-3" aria-hidden="true" />
                Mark all read
              </button>
            )}
            {notifications.length > 0 && (
              <button
                onClick={handleClearAll}
                className="flex items-center gap-1 text-xs font-medium transition-colors hover:text-red-600 dark:hover:text-red-400 focus:outline-none focus-visible:underline focus-visible:text-red-600"
                style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
              >
                <Trash2 className="w-3 h-3" aria-hidden="true" />
                Clear all
              </button>
            )}
          </div>
        </div>

        {/* Notification list */}
        <div className="max-h-[400px] overflow-y-auto">
          {loading ? (
            <div className="p-6 text-center">
              <Bell
                className="w-8 h-8 mx-auto mb-2 animate-pulse"
                style={{ color: "var(--st-border-medium)" }}
              />
              <p
                style={{
                  fontFamily: "Quicksand, sans-serif",
                  color: "var(--st-text-muted)",
                  fontSize: "14px",
                }}
              >
                Loading...
              </p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-6 text-center">
              <BellOff
                className="w-10 h-10 mx-auto mb-3"
                style={{ color: "var(--st-border-medium)" }}
              />
              <p
                className="font-medium mb-1"
                style={{
                  fontFamily: "Quicksand, sans-serif",
                  color: "var(--st-text-heading)",
                  fontSize: "14px",
                }}
              >
                No notifications yet
              </p>
              <p
                style={{
                  fontFamily: "Quicksand, sans-serif",
                  color: "var(--st-text-muted)",
                  fontSize: "12px",
                }}
              >
                When someone likes your story, you'll see it here
              </p>
            </div>
          ) : (
            notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onMarkAsRead={markAsRead}
                onClick={handleNotificationClick}
              />
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
